import {useQuery} from "@tanstack/react-query";
import {UpgradeSubscriptionButton} from "components/action-buttons/action-buttons";
import {ApiKeyTable} from "components/api-keys/api-keys-table";
import {AccessControlModal} from "components/modals/access-control-modal";
import {PermissionsKeys} from "components/permissions/permission-keys";
import {PermissionsReasons} from "components/permissions/permission-messages";
import {PermissionsWrapper} from "components/permissions/permissions";
import {usePermissions} from "components/permissions/usePermissions";
import {ReactQueryErrorWrapper} from "components/shared/react-query-error-wrapper";
import {Alert, AlertAction, AlertDescription, AlertTitle,} from "components/ui/alert";
import {Button} from "components/ui/button";
import {Spinner} from "components/ui/spinner";
import {useIdentity} from "contexts/identity-context";
import * as QueryKeys from "data";
import {fetchApiKeys} from "data/queries-common";
import appsDark from "icons/apps-dark.png";
import appsLight from "icons/apps-light.png";
import {InfoIcon} from "lucide-react";
import {useState} from "react";
import * as Routes from "routes";
import {resolveIdentityId} from "utils/identity";
import {CreateAppDialog} from "./create-app-dialog";

// Matched against ApiKey.products[].productName, so these must be the Apigee product names.
export const premiumProducts = [
    "Buildings API",
    "Land Parcels API",
    "Geoscape Verify",
];

export const ApiKeysPage = () => {
    const [identityState] = useIdentity();
    const identityId = resolveIdentityId(identityState);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    const {isPermitted} = usePermissions();
    const apiKeysQuery = useQuery({
        queryKey: [QueryKeys.apiKeys, identityId],
        queryFn: () => fetchApiKeys(),
    });

    const keysPremiumData = apiKeysQuery.data?.some((key: any) =>
        // products are {productName, productDisplayName} objects, so map before comparing
        key.products
            .map((product: any) => product.productName)
            .some((productName: string) => premiumProducts.includes(productName)),
    );

    const premiumDataPermission = [
        PermissionsKeys.premiumDataCreate,
        PermissionsKeys.premiumDataDelete,
        PermissionsKeys.premiumDataRead,
        PermissionsKeys.premiumDataUpdate,
    ];

    return (
        <div className="space-y-4">
            <PermissionsWrapper
                keys={[PermissionsKeys.appsCreate]}
                messages={{
                    [PermissionsReasons.ROLE_DENY]: <></>,
                    [PermissionsReasons.UPGRADE_REQUIRED]: <></>,
                }}
            >
                <div className="flex justify-end">
                    <Button
                        disabled={apiKeysQuery.isPending}
                        onClick={() => setCreateDialogOpen(true)}
                    >
                        {apiKeysQuery.isPending && <Spinner/>}
                        Create API Key
                    </Button>
                </div>
                <CreateAppDialog
                    open={createDialogOpen}
                    handleClose={() => {
                        setCreateDialogOpen(false);
                    }}
                />
            </PermissionsWrapper>

            {/* We got this double wrapper so we only show you the 'upgrade for premium data' CTA  */}
            <PermissionsWrapper
                keys={premiumDataPermission}
                messages={{
                    [PermissionsReasons.ROLE_DENY]: <></>,
                    [PermissionsReasons.UPGRADE_REQUIRED]: (
                        <PermissionsWrapper
                            keys={[PermissionsKeys.appsCreate]}
                            messages={{
                                [PermissionsReasons.UPGRADE_REQUIRED]: <></>,
                            }}
                            fallbackToChildren={[PermissionsReasons.ROLE_DENY]}
                        >
                            <Alert variant="info" className="">
                                <InfoIcon/>
                                <AlertTitle>
                                    {keysPremiumData
                                        ? "Premium APIs unavailable"
                                        : "Upgrade for premium"}
                                </AlertTitle>
                                <AlertDescription>
                                    {keysPremiumData
                                        ? "Looks like some of your apps use premium data. To enable those APIs and data or create new apps with access to premium data, please upgrade to a paid plan."
                                        : "Want more from My Apps? Upgrade to a paid plan for larger credit balances and access to premium APIs and data such as Land Parcels API, Buildings API, Geoscape Verify and MailPoint."}
                                </AlertDescription>
                                <AlertAction>
                                    <UpgradeSubscriptionButton/>
                                </AlertAction>
                            </Alert>
                        </PermissionsWrapper>
                    ),
                }}
            >
                <></>
            </PermissionsWrapper>

            <div>
                <ReactQueryErrorWrapper queries={[apiKeysQuery]} mutations={[]}/>
                <PermissionsWrapper
                    keys={[PermissionsKeys.appsCreate]}
                    messages={{
                        [PermissionsReasons.UPGRADE_REQUIRED]: (
                            <AccessControlModal
                                title="Subscribe to access My Apps"
                                children={<AppInfo/>}
                                imageLight={appsLight}
                                imageDark={appsDark}
                            />
                        ),
                    }}
                    fallbackToChildren={[PermissionsReasons.ROLE_DENY]}
                >
                    {apiKeysQuery.data && (
                        <ApiKeyTable
                            data={apiKeysQuery.data}
                            isLoading={apiKeysQuery.isPending}
                            apiKeyDetailsRoute={Routes.developerApiKeysEditKey}
                            handleClick={() => {
                            }}
                            identityId={identityId}
                        />
                    )}
                </PermissionsWrapper>
            </div>
        </div>
    );
};

const AppInfo = () => {
    return (
        <div>
            <p className="text-sm text-muted-foreground">
                Integrate address autocomplete, geocoding and maps into your apps and
                websites. Create and manage access through API Keys.
            </p>

            <p className="text-sm text-muted-foreground">
                Premium can additionally return insights on:
            </p>

            <ul className="list-disc pl-6 text-sm text-muted-foreground">
                <li>Buildings</li>
                <li>Solar</li>
                <li>Property</li>
                <li>And more</li>
            </ul>

            <p className="text-sm text-muted-foreground">
                Subscribe to get started with My Apps
            </p>
        </div>
    );
};

export default AppInfo;
